import { CommandCenter } from "#components/CommandCenter";
import { trackCtaClick } from "#utils/utils";
import {
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import React from "react";

interface HeroSectionProps {
  onCtaClick?: () => void;
}

const MotionVStack = motion(VStack);

export const HeroSection: React.FC<HeroSectionProps> = ({ onCtaClick }) => {
  const handleCtaClick = (label: string, url: string) => {
    trackCtaClick({
      cta_label: label,
      cta_location: "Hero",
      destination_url: url,
    });
    if (onCtaClick) {
      onCtaClick();
    } else {
      window.location.href = url;
    }
  };

  const overlayBg = useColorModeValue(
    "whiteAlpha.800", // Light mode: lighter overlay
    "blackAlpha.700", // Dark mode: darker overlay
  );

  const overlayBackdropFilter = useColorModeValue(
    "blur(14px)", // Light mode: subtle blur
    "blur(2px) brightness(40%)", // Dark mode: darker with slight blur
  );

  const textColor = useColorModeValue("gray.900", "white");
  const textShadowValue = useColorModeValue(
    "0px 2px 8px rgba(255,255,255, 0.5)",
    "0px 2px 4px rgba(0,0,0,0.4)",
  );

  const subtextColor = useColorModeValue("gray.800", "whiteAlpha.900");

  return (
    <Box position="relative" py={12} w="full" overflow="hidden">
      {/* Video Background */}
      <Box
        as="video"
        autoPlay
        loop
        muted
        playsInline
        position="absolute"
        top="0"
        left="0"
        w="full"
        h="full"
        objectFit="cover"
        zIndex="-2"
        src="/img/bg/pcback.mp4"
      />

      {/* Dynamic Overlay for Readability */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="full"
        h="full"
        bg={overlayBg}
        backdropFilter={overlayBackdropFilter}
        zIndex="-1"
      />

      <Container
        maxW="container.xl"
        h={{ base: "container", medium: "full" }}
        centerContent
        justifyContent="center"
      >
        <Stack
          direction={{ base: "column", lg: "row" }}
          spacing={{ base: 8, lg: 8 }}
          w="full"
          align="center"
          justify="space-between"
        >
          {/* Left Column: Value Prop & CTAs */}
          <MotionVStack
            spacing={6}
            textAlign={{ base: "center", lg: "left" }}
            color={textColor}
            align={{ base: "center", lg: "flex-start" }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            flex="1"
            maxW={{ lg: "xl", xl: "2xl" }}
          >
            <Heading
              as="h1"
              size="2xl"
              fontWeight="bold"
              letterSpacing="tight"
              textShadow={textShadowValue}
              lineHeight="1.2"
            >
              We eliminate
              {' '}
              <Box
                as="span"
                bgGradient="linear(to-r, blue.400, cyan.400)"
                bgClip="text"
              >
                one business process
              </Box>
              {' '}at a time.
            </Heading>

            <Text
              fontSize="xl"
              opacity={0.9}
              color={subtextColor}
              textShadow={textShadowValue}
            >
              We are a Business Operations Automation firm and Technology Broker. We take your highest-
              friction process—scheduling, intake, onboarding, reporting—and
              build an end-to-end system that runs without your team touching it.
              Plus, we leverage our 200+ technology partnerships to upgrade your telecom and cloud infrastructure at the best possible price.
            </Text>

            <HStack
              spacing={4}
              pt={4}
              flexDir={{ base: "column", md: "row" }}
              w={{ base: "full", md: "auto" }}
            >
              <Button
                size="lg"
                colorScheme="blue"
                onClick={() =>
                  handleCtaClick(
                    "Map My Operations",
                    "/contact-us?interest=discovery",
                  )
                }
                fontWeight="bold"
                px={8}
                w={{ base: "full", md: "auto" }}
                _hover={{
                  bg: "blue.400",
                  transform: "translateY(-2px)",
                  boxShadow: "xl",
                }}
                transition="all 0.2s"
              >
                Map My Operations →
              </Button>
              <Button
                size="lg"
                bg={useColorModeValue("whiteAlpha.800", "whiteAlpha.200")}
                color={useColorModeValue("blue.800", "white")}
                border="1px solid"
                borderColor={useColorModeValue("blue.200", "whiteAlpha.500")}
                onClick={() =>
                  handleCtaClick(
                    "View Solutions",
                    "/solutions",
                  )
                }
                fontWeight="bold"
                px={8}
                w={{ base: "full", md: "auto" }}
                _hover={{
                  bg: useColorModeValue("blue.50", "whiteAlpha.300"),
                  transform: "translateY(-2px)",
                  boxShadow: "xl",
                }}
                transition="all 0.2s"
              >
                See Our Solutions
              </Button>
            </HStack>
          </MotionVStack>

          {/* Right Column: AI & ROI Command Center */}
          <Box
            flex="1"
            w="full"
            as={motion.div}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: "0.8", ease: "easeOut", delay: "0.2" }}
            display="flex"
            justifyContent={{ base: "center", lg: "flex-end" }}
          >
            <CommandCenter />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};
